import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ButtonGroup from '@mui/material/ButtonGroup';
import { byteSize, Translate, TextFormat, getPaginationState, JhiPagination, JhiItemCount } from 'react-jhipster';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { APP_DATE_FORMAT, APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './booking.reducer';

export const Booking = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const bookingList = useAppSelector(state => state.booking.entities);
  const loading = useAppSelector(state => state.booking.loading);
  const totalItems = useAppSelector(state => state.booking.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    } else {
      return order === ASC ? faSortUp : faSortDown;
    }
  };

  return (
    <div>
      <h2 id="booking-heading" data-cy="BookingHeading">
        <Translate contentKey="jhipsterSampleApplicationApp.booking.home.title">Bookings</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="jhipsterSampleApplicationApp.booking.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/booking/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="jhipsterSampleApplicationApp.booking.home.createLabel">Create new Booking</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {bookingList && bookingList.length > 0 ? (
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <MuiTable aria-label="bookings table">
              <TableHead>
                <TableRow>
                  <TableCell onClick={sort('id')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.id">ID</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                  </TableCell>
                  <TableCell onClick={sort('bookDatetime')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.bookDatetime">Book Datetime</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('bookDatetime')} />
                  </TableCell>
                  <TableCell onClick={sort('noPersons')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.noPersons">No Persons</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noPersons')} />
                  </TableCell>
                  <TableCell onClick={sort('noKids')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.noKids">No Kids</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noKids')} />
                  </TableCell>
                  <TableCell onClick={sort('noPets')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.noPets">No Pets</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('noPets')} />
                  </TableCell>
                  <TableCell onClick={sort('total')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.total">Total</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('total')} />
                  </TableCell>
                  <TableCell onClick={sort('paymentType')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.paymentType">Payment Type</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('paymentType')} />
                  </TableCell>
                  <TableCell onClick={sort('valid')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.valid">Valid</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('valid')} />
                  </TableCell>
                  <TableCell onClick={sort('cancelledAt')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.cancelledAt">Cancelled At</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('cancelledAt')} />
                  </TableCell>
                  <TableCell onClick={sort('remoteData')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.remoteData">Remote Data</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('remoteData')} />
                  </TableCell>
                  <TableCell onClick={sort('remoteId')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.remoteId">Remote Id</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('remoteId')} />
                  </TableCell>
                  <TableCell onClick={sort('createdDate')} className="hand">
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.createdDate">Created Date</Translate>{' '}
                    <FontAwesomeIcon icon={getSortIconByFieldName('createdDate')} />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.schedule">Schedule</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell>
                    <Translate contentKey="jhipsterSampleApplicationApp.booking.passenger">Passenger</Translate>{' '}
                    <FontAwesomeIcon icon="sort" />
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {bookingList.map((booking, i) => (
                  <TableRow key={`entity-${i}`} data-cy="entityTable">
                    <TableCell>
                      <Button component={Link} to={`/booking/${booking.id}`} variant="text" size="small">
                        {booking.id}
                      </Button>
                    </TableCell>
                    <TableCell>
                      {booking.bookDatetime ? <TextFormat type="date" value={booking.bookDatetime} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{booking.noPersons}</TableCell>
                    <TableCell>{booking.noKids}</TableCell>
                    <TableCell>{booking.noPets}</TableCell>
                    <TableCell>{booking.total}</TableCell>
                    <TableCell>{booking.paymentType}</TableCell>
                    <TableCell>{booking.valid ? 'true' : 'false'}</TableCell>
                    <TableCell>
                      {booking.cancelledAt ? <TextFormat type="date" value={booking.cancelledAt} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>{booking.remoteData}</TableCell>
                    <TableCell>{booking.remoteId}</TableCell>
                    <TableCell>
                      {booking.createdDate ? <TextFormat type="date" value={booking.createdDate} format={APP_LOCAL_DATE_FORMAT} /> : null}
                    </TableCell>
                    <TableCell>
                      {booking.schedule ? <Link to={`/tour-schedule/${booking.schedule.id}`}>{booking.schedule.code}</Link> : ''}
                    </TableCell>
                    <TableCell>
                      {booking.passenger ? <Link to={`/passenger/${booking.passenger.id}`}>{booking.passenger.name}</Link> : ''}
                    </TableCell>
                    <TableCell className="text-end">
                      <div className="btn-group flex-btn-group-container">
                        <ButtonGroup variant="outlined" size="small" aria-label="entity actions">
                          <Button
                            component={Link}
                            to={`/booking/${booking.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                            color="primary"
                            startIcon={<FontAwesomeIcon icon="pencil-alt" />}
                          >
                          </Button>
                          <Button
                            onClick={() =>
                              (window.location.href = `/booking/${booking.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                            }
                            color="error"
                            startIcon={<FontAwesomeIcon icon="trash" />}
                          >
                          </Button>
                        </ButtonGroup>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="jhipsterSampleApplicationApp.booking.home.notFound">No Bookings found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={bookingList && bookingList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Booking;
